import React from "react";
import { T } from "../theme";
import { shortDate } from "../client/now";

// Moments over time: rows across the engagement month by month, a dot per
// dated item (done filled, in progress in the Thread lime, still to come
// outlined) and today marked. No bars: steps are moments, not durations.
// Used by the client roadmap (a row per phase) and the admin overview (a row
// per engagement; there dots are clickable and the selected one is ringed). Pure SVG scaled to the column; on phones it scrolls
// sideways instead of shrinking (responsive.css `.roadmap-timeline`).
//   rows: [{ key, label, dots: [{ id, label, date, status }] }]
//   from / to: optional month bounds as "YYYY-MM" (default: the dots' span)
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const RIGHT = 12, ROW = 44, TOP = 26, W = 760;

export const monthIndex = iso => { const [y, m] = iso.split("-").map(Number); return y * 12 + (m - 1); };
const dayFraction = iso => { const d = Number(iso.slice(8, 10)) || 1; return (d - 1) / 31; };

// Dots a few days apart would sit on top of each other: nudge near
// neighbours up and down alternately so each stays visible.
function spread(dots) {
  const out = []; let run = 0;
  dots.sort((a, b) => a.cx - b.cx).forEach((d, i) => {
    run = i > 0 && d.cx - dots[i - 1].cx < 12 ? run + 1 : 0;
    out.push({ ...d, dy: run === 0 ? 0 : (run % 2 ? -7 : 7) });
  });
  return out;
}

export function chartRange(rows, today, bounds = {}) {
  const dates = [];
  rows.forEach(r => r.dots.forEach(d => { if (d.date) dates.push(d.date); }));
  if (dates.length === 0) return null;
  const all = [...dates, today].sort();
  const from = bounds.from ? monthIndex(bounds.from) : monthIndex(all[0]);
  const to = bounds.to ? monthIndex(bounds.to) + 1 : monthIndex(all[all.length - 1]) + 1; // exclusive end
  return to > from ? { from, to } : null;
}

export default function TimelineChart({ rows, today, from, to, labelWidth = 92, rowHref, onDotClick, selectedId, shadeMonth, rowNote, noteWidth = 0 }) {
  const dated = rows.filter(r => r.dots.some(d => d.date));
  const range = chartRange(dated, today, { from, to });
  if (!range) return null;
  const inRange = iso => { const mi = monthIndex(iso); return mi >= range.from && mi < range.to; };
  const drawn = dated.filter(r => r.dots.some(d => d.date && inRange(d.date))); // a row with nothing in the window is noise
  if (drawn.length === 0) return null;
  const months = range.to - range.from;
  const LEFT = labelWidth;
  const span = W - LEFT - RIGHT - noteWidth;
  const x = iso => LEFT + ((monthIndex(iso) - range.from + dayFraction(iso)) / months) * span;
  const H = TOP + drawn.length * ROW + 22;
  const every = months > 36 ? 12 : months > 14 ? 2 : 1; // label cadence: monthly, every other, yearly
  const todayX = x(today);
  const fill = d => d.status === "complete" ? T.pine : d.status === "in-progress" ? T.threadline : T.canvas;
  const stroke = d => d.status === "complete" ? T.pine : d.status === "in-progress" ? T.root : d.status === "missed" ? T.statusOff : T.fgMuted;

  return (
    <div className="roadmap-timeline" style={{ marginTop: 8 }}><svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Timeline" style={{ display: "block", fontFamily: T.fontSans }}>
      {Array.from({ length: months + 1 }, (_, i) => {
        const idx = range.from + i; const y = Math.floor(idx / 12), m = idx % 12;
        const gx = LEFT + (i / months) * span;
        return (
          <g key={i}>
            <line x1={gx} x2={gx} y1={TOP - 6} y2={H - 18} stroke={T.borderSubtle} strokeWidth={1} />
            {i < months && i % every === 0 && <text x={gx + 3} y={12} fontSize={10} fill={T.fgMuted}>{MONTHS[m]}{m === 0 || i === 0 ? ` ${String(y).slice(2)}` : ""}</text>}
          </g>
        );
      })}
      {shadeMonth && inRange(shadeMonth + "-01") && <rect x={LEFT + ((monthIndex(shadeMonth + "-01") - range.from) / months) * span} y={TOP - 6} width={span / months} height={H - 18 - (TOP - 6)} fill={T.bgSurfaceSoft} opacity={0.55} />}
      {inRange(today) && <>
        <line x1={todayX} x2={todayX} y1={TOP - 6} y2={H - 18} stroke={T.root} strokeWidth={1.5} strokeDasharray="3 3" />
        <text x={todayX} y={H - 4} fontSize={10} fontWeight={600} fill={T.root} textAnchor="middle">Today</text>
      </>}
      {drawn.map((row, ri) => {
        const cy = TOP + ri * ROW + ROW / 2;
        const label = <text x={row.color ? 15 : 0} y={cy + 4} fontSize={12} fontWeight={600} fill={rowHref ? T.pine : T.root}>{row.label}</text>;
        const note = rowNote ? rowNote(row) : null;
        return (
          <g key={row.key}>
            {row.color && <rect x={0} y={cy - 5} width={9} height={9} rx={2} fill={row.color} />}
            {rowHref ? <a href={rowHref(row)}>{label}</a> : label}
            <line x1={LEFT} x2={LEFT + span} y1={cy} y2={cy} stroke={T.borderSubtle} strokeWidth={1} />
            {note && <text x={LEFT + span + 10} y={cy + 4} fontSize={10} fill={T.fgSecondary}>{note}</text>}
            {/* A window (a Pulse's six weeks, say): a bar from `start` to the due date, under the dot. */}
            {row.dots.filter(d => d.date && d.start && d.start < d.date && inRange(d.date)).map(d => {
              const x1 = Math.max(LEFT, x(d.start)), x2 = x(d.date);
              return <rect key={`w-${d.id}`} x={x1} y={cy - 3} width={Math.max(2, x2 - x1)} height={6} rx={3} fill={row.color || T.pine} opacity={0.22}><title>{d.label} · {shortDate(d.start)} to {shortDate(d.date)}</title></rect>;
            })}
            {spread(row.dots.filter(d => d.date && inRange(d.date)).map(d => ({ d, cx: x(d.date) }))).map(({ d, cx, dy }) => {
              const sel = selectedId && `${row.key}:${d.id}` === selectedId;
              return (
                <g key={d.id} onClick={onDotClick ? () => onDotClick(d, row) : undefined} style={onDotClick ? { cursor: "pointer" } : undefined} data-dot={`${row.key}:${d.id}`}>
                  {sel && <circle cx={cx} cy={cy + dy} r={10} fill="none" stroke={T.root} strokeWidth={1.5} />}
                  <circle cx={cx} cy={cy + dy} r={6} fill={fill(d)} stroke={stroke(d)} strokeWidth={1.5}>
                    <title>{d.label} · {shortDate(d.date)}</title>
                  </circle>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg></div>
  );
}
